import React, { useEffect, useState, useCallback } from 'react'
import {
  EntryPage,
  SignUpForm,
  SignUpEmailForm,
  SignUpSocial,
  AppEntry,
  useTenant,
  getErrorMessage
} from '@emerald-works-nova/auth'
import { Grid, Typography } from '@mui/material'
import { Auth } from '@aws-amplify/auth'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { userRegistrationSlice } from '../../reducers'
import { v4 as uuidv4 } from 'uuid'
import consts from '../../consts'
import { routes } from '../../router/routes'
import { useEvent } from '@emerald-works/react-event-bus-client'
import AuthLayout from '../../components/auth/auth-layout'
import { SuccessModal } from './components/success-modal'
import { setSignUpInfo } from '../../reducers/user-registration/slice'

const SignUpPage = () => {
  const tenant = useTenant()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [isRegistering, setIsRegistering] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [error, setError] = useState({ isError: false, message: null })
  const [emailVerificationError, setEmailVerificationError] = useState({
    isError: false,
    message: null
  })

  const clearEmailVerificationError = () => {
    setEmailVerificationError({ isError: false, message: null })
  }

  const [isValid, setIsValid] = useState(false)
  const defaultValues = useSelector(
    userRegistrationSlice.selectors.selectSingUpInfo
  )

  const getMessageUsingCode = useCallback((code) => {
    if (
      [
        'SELF_REGISTRATION_NOT_SUPPORTED',
        'SELF_REGISTRATION_NOT_ENABLED'
      ].includes(code)
    ) {
      return (
        <Typography>
          <strong>Registration is currently disabled.</strong> Get in touch with
          your Learning & Development Team or get in touch on{' '}
          <strong>mtfbsupport@mindtools.com</strong>
        </Typography>
      )
    }

    if (code === 'SELF_REGISTRATION_MAX_USER_REACHED') {
      return (
        <Typography>
          <strong>There are no seats left. Please, contact your Manager to free up some space for you.</strong>
        </Typography>
      )
    }

    if (code === 'SELF_REGISTRATION_EMAIL_DOMAIN_NOT_ALLOWED') {
      return (
        <Typography>
          <strong>Your email is not authorized to create an account.</strong>{' '}
          Get in touch with your Learning & Development Team or get in touch on{' '}
          <strong>mtfbsupport@mindtools.com</strong>
        </Typography>
      )
    }

    if (code === 'SELF_REGISTRATION_EMAIL_ALREADY_EXISTS') {
      return (
        <Typography>
          <strong>Email is not available.</strong>
        </Typography>
      )
    }

    return <Typography>Unable to verify email address.</Typography>
  }, [])

  const [checkEmailRegistration] = useEvent([
    {
      eventName: 'userEmailChecker',
      onSuccess: (payload) => {
        setIsValid(payload.valid)

        if (!payload.valid) {
          setEmailVerificationError({
            isError: true,
            message: getMessageUsingCode(payload.code)
          })
          return
        }

        dispatch(
          userRegistrationSlice.actions.setSignUpInfo({
            email: payload.email
          })
        )

        clearEmailVerificationError()
      },
      onError: () => {
        setError({ isError: true, message: 'Unable to check email' })
      }
    }
  ])

  const handleValidateEmail = async (event) => {
    event.preventDefault()
    clearEmailVerificationError()
    checkEmailRegistration.trigger({
      email: event.target.email.value,
      userPoolId: tenant.userPoolId
    })
  }

  const resetEmail = (data) => {
    dispatch(
      userRegistrationSlice.actions.setSignUpInfo({
        ...data,
        email: '',
        password: ''
      })
    )
  }

  const userNameProvider = tenant?.loginProviders.find(
    (lp) => lp.providerType === 'USERNAME'
  )

  const handleSignUp = async (data, evt) => {
    evt.preventDefault()
    setIsRegistering(true)
    try {
      const { firstName, lastName, email, password } = data
      const username = `self-${uuidv4()}`

      await Auth.signUp({
        username,
        password,
        attributes: {
          email,
          given_name: firstName,
          family_name: lastName
        },
        clientMetadata: {
          verifyUrl: window.location.host
        }
      })
      setShowSuccessModal(true)
    } catch (e) {
      setError({ isError: true, message: getErrorMessage(e) })
    }
    setIsRegistering(false)
  }

  useEffect(() => {
    dispatch(setSignUpInfo({ email: '' }))
  }, [dispatch])

  useEffect(() => {
    // Ensure b2c register only for club tenant
    if (tenant && tenant.pk === consts.DEFAULT_CLUB_PK) {
      navigate(routes.b2cFreeRegister.path)
    }
  }, [tenant, navigate])

  useEffect(() => {
    if (
      tenant?.tenantType !== 'public' &&
      (!userNameProvider?.selfRegistration ||
        userNameProvider.selfRegistrationDomains?.length === 0)
    ) {
      navigate('/login')
    }
  }, [tenant, navigate, userNameProvider])

  return (
    <AuthLayout>
      <Grid container direction='column' wrap='nowrap'>
        <Grid item style={{ marginTop: 85 }}>
          <EntryPage
            title='Register'
            subtitle='Please enter your information below.'
            showMtLogo
            content={
              defaultValues.email
                ? (
                  <SignUpForm
                    onSignUp={handleSignUp}
                    emailIsValid={isValid}
                    error={error}
                    setError={setError}
                    isSaving={isRegistering}
                    email={defaultValues.email}
                    resetEmail={resetEmail}
                    defaultValues={defaultValues}
                  />
                  )
                : (
                  <SignUpEmailForm
                    onSubmit={handleValidateEmail}
                    loading={checkEmailRegistration.isWorking}
                    error={emailVerificationError}
                    closeError={clearEmailVerificationError}
                  />
                  )
            }
            social={
              tenant?.tenantType === 'public' && (
                <SignUpSocial
                  text='Or sign in with'
                  loginProviders={tenant?.loginProviders}
                />
              )
            }
            entry={
              <AppEntry
                captionText='Already have an account? Please'
                linkText='Sign in'
                link='/login'
              />
            }
          />
        </Grid>
      </Grid>

      <SuccessModal
        open={showSuccessModal}
        handleClose={() => setShowSuccessModal(false)}
        tenantName={tenant.clientName}
      />
    </AuthLayout>
  )
}

export {
  SignUpPage as Component
}
